import { environment } from "../../environments/environment";
import { LookupModel } from "../_model/metadata/lookup.model";

/***************************************************************************/
/* GENERAL                                                                 */
/***************************************************************************/
/**
 * Checks for if a value exists. Will properly handle falsy values.
 */
export function exists(value): boolean {
  return value !== null && value !== undefined && value !== '';
}
/**
 * Checks for if a value is an array.
 */
export function isArray(value): boolean {
  return value && value.constructor === Array;
}
/**
 * Checks for if a value is an object.
 */
export function isObject(value): boolean {
  return value && value.constructor === Object;
}
/**
 * Checks for if a value is a date.
 */
export function isDate(value): boolean {
  return value && value.constructor === Date;
}

/**
 * Detects the date string format "yyyy-mm-ddThh:mm:ss.mssZ".
 * The time portion (T and further) is is optional.
 */
export function isISOString(string: string): boolean {
  return /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d+)?Z?)?$/.test(string);
}

/**
 * Detects a string (or number) in the format "yyyy".
 * Definitely leads to false positives if not used wisely.
 */
export function isProbablyYear(string: string): boolean {
  return /^\d{4}$/.test(string);
}

/**
 * Compares two arrays' contents against each other to see if they're the same.
 * Only designed for string[] at the moment.
 */
export function arraysEqual(array1: string[], array2: string[]): boolean {
  if (array1.length !== array2.length) return false;
  for (let el of array1) {
    if (!array2.includes(el)) {
      return false;
    }
  }
  return true;
}

/**
 * Handles a ChunkLoadError by reloading the website to the updated website on the route provided.
 * Will lose state if not provided in the route.
 */
export function handleChunkLoadError(error: any, route: string) {
  if (!!error && /ChunkLoadError: Loading chunk [\w]+ failed\./.test(error)) {
    window.location.href = environment.url + route.startsWith('/') ? route : ('/' + route);
  } else throw error;
}

/***************************************************************************/
/* SORTING                                                                 */
/***************************************************************************/
/**
 * Sorts the supplied array with the supplied key in the supplied direction, then returns it
 */
export function sort<T>(array: T[], key: keyof T, order: 'ASC' | 'DESC'): typeof array {
  return array.sort((a, b) => compare(a[key], b[key], order));
}

/**
 * Standard sort comparison ternary
 */
function compare(a: any, b: any, order: 'ASC' | 'DESC'): number {
  return (a == b ? 0 : a > b ? 1 : -1) * (order === 'ASC' ? 1 : -1);
}

/***************************************************************************/
/* FORMATTING                                                              */
/***************************************************************************/

export function formatAscii(string: string): string {

  //replace dashes
  // string = string.replace(/[–—‐‑−`]/g, '-');
  string = string.replace(/[`\u2013\u2014\u2010\u2011\u2212`]/g, '-');

  //replace double-quotes
  // string = string.replace(/[“”„‟″‴‶‷]/g, '"');
  string = string.replace(/[\u201C\u201D\u201E\u201F\u2033\u2034\u2036\u2037]/g, '"');

  //replace single-quotes
  // string = string.replace(/[‘’‚‛′‵]/g, "'");
  string = string.replace(/[\u2018\u2019\u201A\u201B\u2032\u2035]/g, "'");

  //replace angle brackets
  // string = string.replace(/[‹«]/g, '<');
  string = string.replace(/[\u2039\u00AB]/g, '<');
  // string = string.replace(/[›»]/g, '>');
  string = string.replace(/[\u203A\u00BB]/g, '>');

  //replace triple-dots
  // string = string.replace(/…/g, '...');
  string = string.replace(/\u2026/g, '...');

  //replace various spaces
  // string = string.replace(/         /g, ' ');
  string = string.replace(/[\u00A0\u2009\u200A\u2002\u2003\u2004\u2005\u2006\u2007]/g, ' ');

  //remove all remaining non-ascii characters
  // string = string.replace(/[^ -~À-ÖØ-Ýà-öø-ÿ]/g, '');
  string = string.replace(/[^\u0020-\u007E\u00C0-\u00D6\u00D8-\u00DD\u00E0-\u00F6\u00F8-\u00FD]/g, '');

  return string;
}

const months: string[] = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
/**
 * Formats a date to: "Month DayNumber, YearNumber"
 */
export function formatDate(date: any): string {
  let d = new Date(date);
  return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

/**
 * Formats a date to: "Month DayNumber, YearNumber HH:MM AM/PM"
 */
export function formatDateTime(date: any, seconds?: boolean): string {
  let d = new Date(date);
  let hours = d.getHours();
  let AMPM = 'AM';
  if (hours >= 12) AMPM = 'PM';
  if (hours > 12) hours -= 12;
  else if (hours === 0) hours = 12;
  let mins: any = d.getMinutes();
  if (mins < 10) mins = '0' + mins;
  let secs: any = d.getSeconds();
  if (secs < 10) secs = '0' + secs;
  return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()} ${hours}:${mins}${seconds ? ':' + secs : ''} ${AMPM}`;
}

/**
 * Formats a date to: "HH:MM AM/PM"
 */
export function formatTime(date: any, seconds?: boolean): string {
  let d = new Date(date);
  let hours = d.getHours();
  let AMPM = 'AM';
  if (hours >= 12) AMPM = 'PM';
  if (hours > 12) hours -= 12;
  else if (hours === 0) hours = 12;
  let mins: any = d.getMinutes();
  if (mins < 10) mins = '0' + mins;
  let secs: any = d.getSeconds();
  if (secs < 10) secs = '0' + secs;
  return `${hours}:${mins}${seconds ? ':' + secs : ''} ${AMPM}`;
}

/**
 * Formats a number/string to: "$dollars.cents"
 */
export function formatCurrency(number: number | string, decimals?: number): string {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals || 0,
    maximumFractionDigits: exists(decimals) ? decimals : 2,
  });
  let currency = formatter.format(formatNumber(number));
  if (currency.includes('.') && currency.indexOf('.') === currency.length - 2 && (decimals != 1)) currency += '0';
  return currency;
}

/**
 * Formats a number/string to actually be a valid number.
 */
export function formatNumber(number: string | number): number {
  //remove non-digits/dashes/decimals
  let string = (''+number).replace(/[^\d.-]/g, '');
  //remove all dashes not at the start
  if (string.lastIndexOf('-') != -1)
    while (string.lastIndexOf('-') !== 0)
      string = string.substring(0, string.lastIndexOf("-")) + string.substring(string.lastIndexOf("-")+1)
  //remove all but the first decimal
  while (string.indexOf('.') !== string.lastIndexOf('.')) {
    string = string.substring(0, string.lastIndexOf('.')) + string.substring(string.lastIndexOf('.') + 1);
  }
  //don't convert empty strings
  if (string.length === 0) return null;
  //convert to number
  return +string;
}

const normalRegex: RegExp = /([^\w]|_)*/g;
/**
 * Normalizes a string to remove all non-alphanumeric characters. Helps a lot when searching against data
 */
export function normalize(string: string): string {
  return (exists(string) && ''+string || '').toUpperCase().replace(normalRegex, '');
}

/**
 * Converts a camelCase string to a PascalCase string
 */
export function camelToPascal(string: string): string {
  return string.substring(0, 1).toUpperCase() + string.substring(1);
}

/**
 * Converts a PascalCase string to a camelCase string
 */
export function pascalToCamel(string: string): string {
  return string.substring(0, 1).toLowerCase() + string.substring(1);
}

/**
 * Converts all the keys in an object to PascalCase
 */
export function pascalizeObject(object: any): any {
  let pascalObject = {};
  for (let key in object)
    pascalObject[camelToPascal(key)] = object[key];
  return pascalObject;
}

/**
 * Converts all the keys in an object to camelCase
 */
export function camelizeObject(object: any): any {
  let camelObject = {};
  for (let key in object)
    camelObject[pascalToCamel(key)] = object[key];
  return camelObject;
}

/**
 * Attempts to format a name properly
 */
export function formatName(name?: string): string {
  return name.toLowerCase().trim()
            .split(' ').filter(part => !!part).map(part => part[0].toUpperCase() + part.substring(1)).join(' ')
            .split('-').filter(part => !!part).map(part => part[0].toUpperCase() + part.substring(1)).join('-')
            .split('.').filter(part => !!part).map(part => part[0].toUpperCase() + part.substring(1)).join('.');
}

/**
 * Replaces the end of the string supplied with the suffix, appends otherwise.
 */
export function replaceSuffix(string: string, replace: string, suffix: string): string {
  return (string || '').replace(replace || '', '') + suffix || '';
}

/**
 * Replaces the contents of the string supplied with the replacement.
 */
export function replaceString(string: string, replace: string, replacement: string): string {
  return (string || '').replace(replace || '', replacement);
}

/**
 * Builds address line 1, 2, and also a full address string.
 */
export function buildAddresses(
  streetNumber: string, streetDirPrefix: string, streetName: string, streetSuffix: string, streetDirSuffix: string, unitNumber: string,
  city: string, stateOrProvince: string, postalCode: string, country?: string, lookups?: LookupModel[]
): {
  address1: string,
  address2: string,
  fullAddress: string,
} {
  let addresses = {
    address1: null,
    address2: null,
    fullAddress: null,
  };
  addresses.address1 = buildAddress1(streetNumber, streetDirPrefix, streetName, streetSuffix, streetDirSuffix, unitNumber);
  addresses.address2 = buildAddress2(city, stateOrProvince, postalCode, country, lookups);
  addresses.fullAddress = addresses.address1 + ' ' + addresses.address2;
  return addresses;
}

/**
 * Builds address line 1
 */
function buildAddress1(streetNumber: string, streetDirPrefix: string, streetName: string, streetSuffix: string, streetDirSuffix: string, unitNumber: string) {
  let address = '';
  address += exists(streetNumber) ? streetNumber : '';
  address += exists(streetDirPrefix) ? ' ' + streetDirPrefix : '';
  address += exists(streetName) ? ' ' + streetName : '';
  address += exists(streetSuffix) && normalize(streetSuffix) !== normalize('N/A') ? ' ' + streetSuffix : '';
  address += exists(streetDirSuffix) ? ' ' + streetDirSuffix : '';
  address += exists(unitNumber) ? ', ' + unitNumber : '';
  return address;
}

/**
 * Builds address line 2
 */
function buildAddress2(city?: string, stateOrProvince?: string, postalCode?: string, country?: string, lookups?: LookupModel[]) {
  let address = '';
  if (city && lookups?.length) {
    city = lookups.find(l => l.lookupValue === city)?.lookupDisplayName || city;
  }
  city = city?.replace(/Toronto(\ )?[CEW]\d{2}/, 'Toronto');
  city = city?.replace(/(\ )?and(\ )?Area/i, '');
  address += exists(city) ? city : '';
  if (stateOrProvince && lookups?.length) {
    stateOrProvince = lookups.find(l => l.lookupValue === stateOrProvince)?.lookupDisplayName || stateOrProvince;
  }
  address += exists(stateOrProvince) ? ((exists(city) ? ', ' : '') + stateOrProvince) : '';
  address += exists(postalCode) ? ' ' + postalCode : '';
  address += exists(country) ? ', ' + country : '';
  return address;
}
